/**
 * REC-CONF-2: Shared PO Receipt Outcome Badge
 *
 * Display priority:
 *   1. If a PO exception exists → show exception badge (amber)
 *   2. Otherwise if receiptStatus exists → show receipt outcome badge
 *   3. Otherwise → render nothing
 *
 * Exception always overrides receipt status visually.
 */

import { extractPOExceptionFromReceiptNotes } from '../../utils/extractPOExceptionFromReceiptNotes';
import { formatPOException } from '../../utils/formatPOException';

interface POReceiptOutcomeBadgeProps {
  /** The receipt lifecycle status (complete, short, over, damaged, refused) */
  receiptStatus?: string | null;
  /** Raw receipt notes — exception is extracted from here */
  receiptNotes?: string | null;
  /** Size variant */
  size?: 'xs' | 'sm';
}

/** Color config for receipt status */
const STATUS_COLORS: Record<string, string> = {
  complete:
    'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
  short:
    'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800',
  over:
    'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
  // Legacy values still in DB from before taxonomy correction
  damaged:
    'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800',
  refused:
    'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800',
};

/** Color config for exceptions — always amber to signal advisory */
const EXCEPTION_COLOR =
  'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800';

/** Display labels for receipt status */
const STATUS_LABELS: Record<string, string> = {
  complete: '✓ Complete',
  short: '▼ Short',
  over: '▲ Over',
  damaged: '✕ Damaged',
  refused: '✕ Refused',
};

export function POReceiptOutcomeBadge({
  receiptStatus,
  receiptNotes,
  size = 'xs',
}: POReceiptOutcomeBadgeProps) {
  const exceptionType = extractPOExceptionFromReceiptNotes(receiptNotes);
  const exceptionLabel = exceptionType ? formatPOException(exceptionType) : null;

  const sizeClass = size === 'sm' ? 'text-xs px-2.5 py-0.5' : 'text-[11px] px-2 py-0.5';

  // Priority 1: Exception overrides everything
  if (exceptionLabel) {
    return (
      <span
        className={`inline-flex items-center gap-1 font-bold uppercase tracking-wider rounded border ${EXCEPTION_COLOR} ${sizeClass}`}
      >
        ⚠ {exceptionLabel}
      </span>
    );
  }

  // Priority 2: Receipt status
  if (receiptStatus) {
    const colorClass = STATUS_COLORS[receiptStatus] || STATUS_COLORS.complete;
    const label = STATUS_LABELS[receiptStatus] || receiptStatus.toUpperCase();
    return (
      <span
        className={`inline-flex items-center gap-1 font-bold uppercase tracking-wider rounded border ${colorClass} ${sizeClass}`}
      >
        {label}
      </span>
    );
  }

  // Priority 3: Nothing
  return null;
}

/**
 * Derive the highest-priority exception from an array of linked POs.
 * Returns the first exception found, or null.
 */
export function getFirstPOException(
  linkedPOs?: { receiptNotes?: string | null }[]
): string | null {
  if (!linkedPOs) return null;
  for (const po of linkedPOs) {
    const ex = extractPOExceptionFromReceiptNotes(po.receiptNotes);
    if (ex) return formatPOException(ex);
  }
  return null;
}

/**
 * Summarize receipt outcomes across linked POs.
 * Returns a compact string like "2/3 received · 1 short" or null.
 */
export function summarizeReceiptOutcomes(
  linkedPOs?: { receiptStatus?: string | null; receiptNotes?: string | null }[]
): { summary: string; hasException: boolean } | null {
  if (!linkedPOs || linkedPOs.length === 0) return null;

  let complete = 0;
  let short = 0;
  let over = 0;
  let pending = 0;
  let hasException = false;

  for (const po of linkedPOs) {
    const ex = extractPOExceptionFromReceiptNotes(po.receiptNotes);
    if (ex) hasException = true;

    const st = po.receiptStatus;
    if (!st) { pending++; continue; }
    if (st === 'complete') complete++;
    else if (st === 'short') short++;
    else if (st === 'over') over++;
    else complete++; // Legacy damaged/refused count as received
  }

  const parts: string[] = [];
  if (complete > 0) parts.push(`${complete} complete`);
  if (short > 0) parts.push(`${short} short`);
  if (over > 0) parts.push(`${over} over`);
  if (pending > 0) parts.push(`${pending} pending`);

  return parts.length > 0
    ? { summary: parts.join(' · '), hasException }
    : null;
}
